/**
 * The one HTTP client. Every request to the governance API goes through `request`.
 *
 * Same base-URL normalization, bearer token and error shape as the client it was ported from.
 */

/** Browser storage keys, scoped to the path this build is served under (a root build keeps the plain name). */
export function storageKey(name: string, base: string = import.meta.env.BASE_URL ?? '/'): string {
  const path = base.replace(/\/+$/, '')
  return path === '' ? name : `${name}@${path}`
}

/** The session key. */
export const sessionKey = (name: 'token' | 'user'): string => storageKey(`rithmo_${name}`)

/** Accepts `http://host:port`, `http://host:port/api` or a relative `/api` and always returns the `/api` mount. */
export function resolveApiBaseUrl(raw: string | undefined): string {
  // Same-origin default: the customer product serves the SPA + governance API from one origin (nginx proxies /api), and
  // the dev server proxies /api too. Vite does not inline VITE_API_URL from a shell env, so a dev-host absolute default
  // would otherwise bake into the customer bundle (and break every API call on the VM).
  const fallback = '/api'
  const value = raw?.trim()
  if (!value) return fallback
  const trimmed = value.replace(/\/+$/, '')
  if (trimmed.endsWith('/api')) return trimmed
  if (trimmed.startsWith('/')) return trimmed === '' ? '/api' : `${trimmed}/api`
  try {
    const url = new URL(trimmed)
    if (!url.pathname || url.pathname === '/') return `${url.origin}/api`
  } catch {
    /* not an absolute URL; fall through */
  }
  return `${trimmed}/api`
}

const API_BASE_URL = resolveApiBaseUrl(import.meta.env.VITE_API_URL)

/** Origin of the API without the `/api` path, used to show agents the MCP endpoint they should call. */
export function apiOrigin(): string {
  return API_BASE_URL.replace(/\/api\/?$/, '') || window.location.origin
}

export class ApiError extends Error {
  status: number
  code: string | null
  /** The response body, for an endpoint whose refusal is a typed answer. */
  body: unknown

  constructor(message: string, status: number, code: string | null, body?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.body = body
  }
}

export const tokenStore = {
  get: (): string | null => {
    try { return localStorage.getItem(sessionKey('token')) } catch { return null }
  },
  set: (token: string) => {
    try { localStorage.setItem(sessionKey('token'), token) } catch { /* storage unavailable */ }
  },
  clear: () => {
    try { localStorage.removeItem(sessionKey('token')) } catch { /* storage unavailable */ }
  },
}

let onUnauthorized: (() => void) | null = null

/** The auth provider registers this so an expired session signs the user out instead of leaving broken pages. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler
}

type ErrorBody = { message?: string; error?: { message?: string; code?: string } | string; code?: string }

function errorFrom(status: number, body: ErrorBody): ApiError {
  const nested = typeof body.error === 'object' && body.error !== null ? body.error : null
  const message = nested?.message ?? (typeof body.error === 'string' ? body.error : undefined) ?? body.message ?? `HTTP ${status}`
  const code = nested?.code ?? (typeof body.code === 'string' ? body.code : null) ?? (typeof body.error === 'string' ? body.error : null)
  return new ApiError(message, status, code, body)
}

/** The company API is served by this dashboard's own server, beside the app (under its base path). */
export const COMPANY_API_BASE = (import.meta.env.VITE_COMPANY_API_URL?.trim() || `${import.meta.env.BASE_URL}api/company`).replace(/\/+$/, '')

/** A request against the governance API. */
export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  return requestFrom<T>(API_BASE_URL, path, init)
}

/** A request against any of the dashboard's APIs, with the same session, error shape and logging. */
export async function requestFrom<T>(base: string, path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  }
  const token = tokenStore.get()
  if (token) headers.Authorization = `Bearer ${token}`

  let response: Response
  try {
    response = await fetch(`${base}${path}`, { ...init, headers })
  } catch {
    throw new ApiError('The service could not be reached.', 0, 'network_error')
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as ErrorBody
    const error = errorFrom(response.status, body)
    if (response.status === 401) onUnauthorized?.()
    console.error(`API error [${path}]`, error.status, error.message)
    throw error
  }

  const json = (await response.json()) as unknown
  // Some endpoints wrap their payload as `{ success, data }`; callers always receive the payload itself.
  if (json && typeof json === 'object' && 'data' in json && 'success' in json) return (json as { data: T }).data
  return json as T
}

/** A business-readable message for a failed request. The raw message stays in the console. */
export function describeError(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    if (error.status === 0) return 'The service could not be reached. Check that it is running and try again.'
    if (error.status === 403 && error.code === 'not_admin') return 'This view is available to workspace admins only.'
    if (error.status === 403 && error.code === 'no_org') return 'Your account is not part of a workspace yet.'
  }
  return fallback
}
